/**
 * @file XCOMM Matching Queue Reducer.
 */

import {
  MATCH_FETCH_VOTES_REQUEST,
  MATCH_FETCH_VOTES_SUCCESS,
  MATCH_QUEUE_VOTES,
  MATCH_COMMIT_VOTES,
  MATCH_UNDO,
  MATCH_CLEAR_REQUEST_TIMEOUTS,
  MATCH_QUEUE_RESET,
} from '../actions/match-queue-actions';
import {
  QUEUE_EXTEND_LEASES,
  QUEUE_DROP_LEASES,
} from '../actions/queue-actions';
import { extendLeaseBatch } from '../../utilities/leasing';

/**
 * Helper factory for creating vote batches for a matching queue (either the main queue
 * or the pending queue). This is to ensure consistency and provide data schema
 * clarity.
 *
 * @param {Object} batch - The batch.
 * @returns {{leaseId: string, expiresAt: number, selectedIdentity: Object, votes: Array, batchId: string, timeoutId: number}}
 */
const createVoteBatch = batch => ({
  // All batches have this info. leaseId can be shared between different batches.
  leaseId: batch.leaseId,
  expiresAt: batch.expiresAt,
  selectedIdentity: batch.selectedIdentity,
  votes: batch.votes,

  // Necessary for pending queue batches. batchId and timeoutId are unique.
  batchId: batch.batchId,
  timeoutId: batch.timeoutId,
});

const isNullBatch = lease => {
  return lease.votes.length === 0;
};

const initialState = {
  queue: [],
  pendingQueue: [],
  requestTimeoutIds: [],
  loading: false,
  done: false,
};

const matchQueueReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case MATCH_FETCH_VOTES_REQUEST:
      return {
        ...state,
        loading: true,
        requestTimeoutIds: [action.timeoutId, ...state.requestTimeoutIds],
      };

    case MATCH_FETCH_VOTES_SUCCESS: {
      const lease = createVoteBatch({
        leaseId: action.lease.lease_id,
        expiresAt: action.lease.lease_expires_at,
        selectedIdentity: action.lease.selected_identity,
        votes: action.lease.face_match_votes,
      });
      const isDone = isNullBatch(lease);

      return {
        ...state,
        queue: isDone ? [...state.queue] : [...state.queue, lease],
        requestTimeoutIds: [],
        loading: false,
        done: isDone,
      };
    }

    case MATCH_QUEUE_VOTES: {
      const [head, ...rest] = state.queue;

      // Match queuing only happens at the head of the main queue
      // so leaseId, expiresAt, and selectedIdentity can all be safely
      // taken from it.
      const batch = createVoteBatch({
        leaseId: head.leaseId,
        expiresAt: head.expiresAt,
        selectedIdentity: head.selectedIdentity,
        votes: action.votes,
        batchId: action.batchId,
        timeoutId: action.timeoutId,
      });

      const voteIds = action.votes.map(({ id }) => id);
      const newHead = {
        ...head,
        votes: head.votes.filter(vote => !voteIds.includes(vote.id)),
      };

      // If the head of the main queue is empty of votes discard it, otherwise
      // filter out the matched votes.
      const queue = newHead.votes.length === 0 ? rest : [newHead, ...rest];

      return {
        ...state,
        queue,
        pendingQueue: [batch, ...state.pendingQueue],
      };
    }

    case MATCH_UNDO: {
      const [head, ...rest] = state.pendingQueue;

      return {
        ...state,
        queue: [head, ...state.queue],
        pendingQueue: rest,
      };
    }

    case MATCH_COMMIT_VOTES: {
      return {
        ...state,
        pendingQueue: state.pendingQueue.filter(
          ({ batchId }) => batchId !== action.batchId,
        ),
      };
    }

    case MATCH_CLEAR_REQUEST_TIMEOUTS: {
      return {
        ...state,
        requestTimeoutIds: [],
      };
    }

    case MATCH_QUEUE_RESET: {
      return {
        ...state,
        queue: [],
        done: false,
        loading: false,
      };
    }

    case QUEUE_EXTEND_LEASES: {
      return {
        ...state,
        queue: state.queue.map(batch => extendLeaseBatch(batch, action)),
        pendingQueue: state.pendingQueue.map(batch =>
          extendLeaseBatch(batch, action),
        ),
      };
    }

    // Pending queue leases shouldn't be dropped.
    case QUEUE_DROP_LEASES: {
      return {
        ...state,
        queue: state.queue.filter(
          batch => !action.leaseIds.includes(batch.leaseId),
        ),
      };
    }

    default:
      return state;
  }
};

export { initialState };
export default matchQueueReducer;
